import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { techScoutApi } from "../services/api";
import { useNotification } from "../context/NotificationContext";
import ExternalSkillReviewPanel from "../components/ExternalSkillReviewPanel";

const DECISION_LABELS = {
  use:    { label: "השתמש", color: "bg-green-100 text-green-700 border-green-300" },
  adapt:  { label: "התאם", color: "bg-blue-100 text-blue-700 border-blue-300" },
  watch:  { label: "עקוב", color: "bg-amber-100 text-amber-700 border-amber-300" },
  ignore: { label: "התעלם", color: "bg-gray-100 text-gray-500 border-gray-300" },
  reject: { label: "דחה", color: "bg-red-100 text-red-600 border-red-300" },
};

const STATUS_BADGE = {
  pending:   "bg-gray-50 text-gray-500 border-gray-200",
  watching:  "bg-amber-50 text-amber-700 border-amber-200",
  piloting:  "bg-blue-50 text-blue-700 border-blue-200",
  adopted:   "bg-green-50 text-green-700 border-green-200",
  ignored:   "bg-gray-50 text-gray-400 border-gray-200",
  rejected:  "bg-red-50 text-red-500 border-red-200",
};

const STATUS_HE = {
  pending:   "ממתין",
  watching:  "במעקב",
  piloting:  "פיילוט",
  adopted:   "אומץ",
  ignored:   "מוסתר",
  rejected:  "נדחה",
};

export function DiscoveryCard({ disc, onDecide, deciding }) {
  const [showReason, setShowReason] = useState(false);
  const [reason, setReason] = useState("");
  const [pendingDecision, setPendingDecision] = useState(null);
  const [redeciding, setRedeciding] = useState(false);
  // The review panel is mounted only while it is open, so the contract request happens when an
  // operator asks for it and not once per row on every render of the list.
  const [reviewing, setReviewing] = useState(false);

  function handleDecide(d) {
    if (d === "ignore" || d === "reject") {
      setPendingDecision(d);
      setShowReason(true);
    } else {
      onDecide(disc.id, { decision: d });
      setRedeciding(false);
    }
  }

  function submitWithReason() {
    onDecide(disc.id, { decision: pendingDecision, decision_reason: reason || undefined });
    setShowReason(false);
    setReason("");
    setRedeciding(false);
  }

  const decided = !!disc.decision;

  return (
    <div className={`bg-white border rounded-xl p-4 space-y-2 ${disc.status === "ignored" || disc.status === "rejected" ? "opacity-60" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-800">{disc.technology_name}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_BADGE[disc.status] || STATUS_BADGE.pending}`}>
              {STATUS_HE[disc.status] || disc.status}
            </span>
            <span className="text-xs text-gray-400 border border-gray-200 rounded-full px-2 py-0.5">{disc.category}</span>
          </div>
          {disc.description && <p className="text-sm text-gray-600 mt-1">{disc.description}</p>}
          {disc.relevance_summary && (
            <p className="text-xs text-brand-700 bg-brand-50 border border-brand-100 rounded px-2 py-1 mt-1">
              💡 {disc.relevance_summary}
            </p>
          )}
          {disc.url && (
            <a href={disc.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-block">
              {disc.url}
            </a>
          )}
          {disc.extra && (disc.extra.last_seen || disc.extra.evidence_history?.length > 0) && (
            <div className="text-xs text-gray-400 mt-1 space-x-2 rtl:space-x-reverse flex flex-wrap gap-x-3 gap-y-0.5">
              {disc.extra.evidence_history?.length > 0 && (
                <span>🔍 נמצא {disc.extra.evidence_history.length}× ב-Scout</span>
              )}
              {disc.extra.last_seen && (
                <span>נראה לאחרונה: {new Date(disc.extra.last_seen).toLocaleDateString("he-IL")}</span>
              )}
              {disc.extra.first_seen && !disc.extra.last_seen && (
                <span>ראשון נגלה: {new Date(disc.extra.first_seen).toLocaleDateString("he-IL")}</span>
              )}
            </div>
          )}
          {disc.attention_score >= 7 && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5 mt-1">
              ⚡ דחוף · {disc.attention_score}
            </span>
          )}
        </div>
        {disc.decision && (
          <span className={`text-xs font-medium px-2 py-1 rounded border shrink-0 ${DECISION_LABELS[disc.decision]?.color || ""}`}>
            {DECISION_LABELS[disc.decision]?.label || disc.decision}
          </span>
        )}
      </div>

      {decided && !redeciding && !showReason && (
        <button
          onClick={() => setRedeciding(true)}
          className="text-xs text-brand-600 hover:underline pt-0.5"
        >
          שנה החלטה
        </button>
      )}

      {disc.decision_reason && (
        <p className="text-xs text-gray-500 bg-gray-50 rounded px-2 py-1">{disc.decision_reason}</p>
      )}
      {disc.decided_by && (
        <p className="text-xs text-gray-400">
          הוחלט על ידי {disc.decided_by}
          {disc.decided_at && <span> · {new Intl.DateTimeFormat("he-IL", { dateStyle: "short" }).format(new Date(disc.decided_at))}</span>}
        </p>
      )}

      {(!decided || redeciding) && !showReason && (
        <div className="flex gap-2 flex-wrap pt-1">
          {["use", "adapt", "watch", "ignore", "reject"].map((d) => (
            <button
              key={d}
              disabled={deciding}
              onClick={() => handleDecide(d)}
              className={`text-xs px-3 py-1 rounded-full border font-medium transition-colors hover:opacity-80 disabled:opacity-40 ${DECISION_LABELS[d].color}`}
            >
              {DECISION_LABELS[d].label}
            </button>
          ))}
        </div>
      )}

      {showReason && (
        <div className="space-y-2 pt-1">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="סיבה (אופציונלי)"
            className="w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
            dir="rtl"
          />
          <div className="flex gap-2">
            <button
              onClick={submitWithReason}
              disabled={deciding}
              className={`text-xs px-3 py-1 rounded-full border font-medium ${DECISION_LABELS[pendingDecision]?.color}`}
            >
              אשר
            </button>
            <button
              onClick={() => { setShowReason(false); setPendingDecision(null); setReason(""); }}
              className="text-xs px-3 py-1 rounded-full border border-gray-300 text-gray-600"
            >
              ביטול
            </button>
          </div>
        </div>
      )}

      {/* The way into the external-Skill review pipeline. It is deliberately NOT one of the five
          decision arms above: those record an opinion about a technology, and this asks the server to
          run §3's admission chain over a candidate and show the provenance it found. */}
      <div className="pt-1">
        <button
          onClick={() => setReviewing((open) => !open)}
          aria-expanded={reviewing}
          className="text-xs text-brand-700 hover:underline"
          data-testid={`skill-review-open-${disc.id}`}
        >
          {reviewing ? "הסתר בדיקת מועמד" : "בדוק כמועמד מיומנות חיצוני"}
        </button>
      </div>

      {reviewing && (
        <ExternalSkillReviewPanel discovery={disc} onClose={() => setReviewing(false)} />
      )}
    </div>
  );
}

function AskInnovationPanel() {
  const [query, setQuery] = useState("");
  const [answer, setAnswer] = useState(null);

  const askMutation = useMutation({
    mutationFn: (q) => techScoutApi.ask(q),
    onSuccess: (data) => setAnswer(data),
  });

  function handleAsk() {
    if (!query.trim() || askMutation.isPending) return;      // each call is a paid LLM request: the Enter key had no guard
    setAnswer(null);
    askMutation.mutate(query.trim());
  }

  return (
    <div className="bg-white border border-brand-100 rounded-xl shadow-sm p-5 space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="font-semibold text-gray-700">🤖 Ask Innovation</h2>
        <span className="text-xs text-gray-400">שאל שאלה על מאגר הגילויים הטכנולוגיים</span>
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAsk()}
          placeholder='לדוגמה: "אילו כלי RMM מצאנו?" או "מה הסטטוס של CIPP?"'
          className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
          dir="rtl"
        />
        <button
          onClick={handleAsk}
          disabled={askMutation.isPending || !query.trim()}
          className="shrink-0 bg-brand-600 hover:bg-brand-700 text-white text-sm px-4 py-2 rounded-lg font-medium disabled:opacity-50 transition-colors"
        >
          {askMutation.isPending ? "שואל..." : "שאל"}
        </button>
      </div>
      {askMutation.isError && (
        <p className="text-sm text-red-600">
          {askMutation.error?.response?.data?.detail || "שגיאה בשליחת השאלה. נסה שוב."}
        </p>
      )}
      {answer && (
        <div className="bg-brand-50 border border-brand-100 rounded-lg px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap" dir="rtl">
          {answer.answer}
          <div className="text-xs text-gray-400 mt-2">
            מודל: {answer.model} · {answer.discovery_count} גילויים הועברו כהקשר
          </div>
        </div>
      )}
    </div>
  );
}

const CATEGORY_LABELS = {
  backend:  "צד שרת",
  ai:       "AI / LLM",
  security: "אבטחה",
  infra:    "תשתית",
  dev:      "פיתוח",
};

const CATEGORY_COLORS = {
  backend:  "bg-blue-50  text-blue-700  border-blue-200",
  ai:       "bg-violet-50 text-violet-700 border-violet-200",
  security: "bg-red-50   text-red-700   border-red-200",
  infra:    "bg-amber-50 text-amber-700 border-amber-200",
  dev:      "bg-gray-50  text-gray-600  border-gray-200",
};

function CatalogTable({ entries }) {
  const grouped = {};
  for (const e of entries) {
    if (!grouped[e.category]) grouped[e.category] = [];
    grouped[e.category].push(e);
  }

  return (
    <div className="space-y-5">
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <h3 className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border mb-2 ${CATEGORY_COLORS[cat] || CATEGORY_COLORS.dev}`}>
            {CATEGORY_LABELS[cat] || cat}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {items.map((e) => (
              <div key={e.import_name} className="flex items-center justify-between bg-white border rounded-lg px-3 py-2">
                <span className="text-sm font-medium text-gray-700">{e.display_name}</span>
                {e.installed_version ? (
                  <span className="text-xs font-mono text-gray-500 bg-gray-50 border rounded px-1.5 py-0.5">
                    v{e.installed_version}
                  </span>
                ) : (
                  <span className="text-xs text-gray-300">לא מותקן</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(new Date(iso));
}

export default function TechScoutPage() {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [runMsg, setRunMsg] = useState(null);
  const [discFilter, setDiscFilter] = useState("all");

  const { data: catalog, isLoading: catalogLoading } = useQuery({
    queryKey: ["tech-scout-catalog"],
    queryFn: techScoutApi.catalog,
    staleTime: 60000,
  });

  const { data: statusData } = useQuery({
    queryKey: ["tech-scout-status"],
    queryFn: techScoutApi.status,
    refetchInterval: 30000,
  });

  const { data: discoveries = [], isLoading: discLoading, isError: discFailed } = useQuery({
    queryKey: ["tech-discoveries"],
    queryFn: () => techScoutApi.discoveries(),
    staleTime: 30000,
  });

  const runMutation = useMutation({
    mutationFn: techScoutApi.run,
    onSuccess: (res) => {
      setRunMsg(`סריקה התחילה — ריצה ${res.run_id.slice(0, 8)}…`);
      qc.invalidateQueries({ queryKey: ["tech-scout-status"] });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setRunMsg(typeof detail === "string" && detail ? detail : "שגיאה בהפעלת הסריקה");
    },
  });

  const decideMutation = useMutation({
    mutationFn: ({ id, payload }) => techScoutApi.decide(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tech-discoveries"] }),
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בשמירת ההחלטה", "error"),
  });

  const installed = catalog?.entries?.filter((e) => e.installed_version) || [];

  const attentionDisc = discoveries
    .filter((d) => d.status === "pending" && (d.attention_score ?? 0) >= 7)
    .sort((a, b) => (b.attention_score ?? 0) - (a.attention_score ?? 0));
  const filteredDisc = discFilter === "all"
    ? discoveries
    : discFilter === "attention"
      ? attentionDisc
      : discoveries.filter((d) => d.status === discFilter);
  const pendingCount = discoveries.filter((d) => !d.decision).length;

  return (
    <div className="max-w-5xl mx-auto space-y-5" dir="rtl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">סיור טכנולוגיות 🔍</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            סריקת גרסאות חבילות, זיהוי עדכונים נדרשים, והמלצות טכנולוגיות
          </p>
        </div>
        <button
          onClick={() => { setRunMsg(null); runMutation.mutate(); }}
          disabled={runMutation.isPending || !statusData?.agent_enabled}
          className="shrink-0 bg-brand-600 hover:bg-brand-700 text-white text-sm px-4 py-2 rounded-lg font-medium disabled:opacity-50 transition-colors"
        >
          {runMutation.isPending ? "מנתח..." : "הפעל סריקת AI"}
        </button>
      </div>

      {/* Scout status */}
      {statusData && (
        <div className="bg-white border rounded-xl px-5 py-3 flex flex-wrap gap-6 text-sm">
          <div>
            <span className="text-gray-400">סוכן: </span>
            <span className={statusData.agent_enabled ? "text-green-600 font-medium" : "text-red-500"}>
              {statusData.agent_enabled ? "פעיל" : "מושבת"}
            </span>
          </div>
          {statusData.last_run_status && (
            <div>
              <span className="text-gray-400">ריצה אחרונה: </span>
              <span className="font-medium text-gray-700">{
                { pending: "מאתחל", analyzing: "מנתח", awaiting_technician_review: "ממתין לסקירה", awaiting_approval: "ממתין לאישור", completed: "הושלם", resolved: "נפתרה", failed: "נכשל", rejected: "נדחה", executing: "מבצע", diagnosing: "מאבחן", paused: "הופסק" }[statusData.last_run_status] ?? statusData.last_run_status
              }</span>
            </div>
          )}
          {statusData.last_run_at && (
            <div>
              <span className="text-gray-400">בוצע: </span>
              {fmtDate(statusData.last_run_at)}
            </div>
          )}
          {statusData.last_run_id && (
            <Link to={`/agent-runs/${statusData.last_run_id}`} className="text-brand-600 hover:underline">
              ראה תוצאה ←
            </Link>
          )}
        </div>
      )}

      {runMsg && (
        <div className="bg-brand-50 border border-brand-200 text-brand-700 rounded-lg px-4 py-2 text-sm">
          {runMsg}
        </div>
      )}

      {/* Discovery Registry */}
      <div className="bg-white border rounded-xl shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-gray-700">מאגר גילויים טכנולוגיים</h2>
            {pendingCount > 0 && (
              <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
                {pendingCount} ממתינים להחלטה
              </span>
            )}
            {attentionDisc.length > 0 && (
              <span className="text-xs bg-orange-100 text-orange-700 border border-orange-300 rounded-full px-2 py-0.5">
                ⚡ {attentionDisc.length} דחוף
              </span>
            )}
          </div>
          <div className="flex gap-1 text-xs flex-wrap">
            {["all", "attention", "pending", "watching", "piloting", "adopted", "ignored", "rejected"].map((s) => (
              <button
                key={s}
                onClick={() => setDiscFilter(s)}
                className={`px-2 py-1 rounded-full border transition-colors ${
                  discFilter === s
                    ? s === "attention"
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-brand-600 text-white border-brand-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                }`}
              >
                {s === "all" ? "הכל" : s === "attention" ? "⚡ דחוף" : STATUS_HE[s] || s}
              </button>
            ))}
          </div>
        </div>

        {discLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="animate-pulse h-16 bg-gray-100 rounded-xl" />)}
          </div>
        ) : discFailed && filteredDisc.length === 0 ? (
          <p role="alert" className="text-sm text-red-600 text-center py-4">לא ניתן לטעון את הגילויים</p>
        ) : filteredDisc.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">אין גילויים בקטגוריה זו</p>
        ) : (
          <div className="space-y-3">
            {filteredDisc.map((disc) => (
              <DiscoveryCard
                key={disc.id}
                disc={disc}
                deciding={decideMutation.isPending && decideMutation.variables?.id === disc.id}
                onDecide={(id, payload) => decideMutation.mutate({ id, payload })}
              />
            ))}
          </div>
        )}
      </div>

      {/* Ask Innovation */}
      <AskInnovationPanel />

      {/* Catalog */}
      <div className="bg-white border rounded-xl shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-700">מחסנית טכנולוגית</h2>
          {catalog && (
            <span className="text-xs text-gray-400">
              {installed.length} חבילות מותקנות · Python {catalog.python_version.split(" ")[0]}
            </span>
          )}
        </div>

        {catalogLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse h-8 bg-gray-100 rounded" />
            ))}
          </div>
        ) : catalog ? (
          <CatalogTable entries={catalog.entries} />
        ) : null}
      </div>
    </div>
  );
}
